import { useGateways } from "../context/GatewayContext";
import ContentFramework from "../components/ContentFramework";
import { useWalletContext } from "../context/WalletContext";
import GatewayPageHeader from '../components/GatewayPageHeader';
import { useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import PageContainer from "../components/layout/PageContainer";
import { FC } from "react";
import { WrenchScrewdriverIcon, BoltIcon, ClockIcon, CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, ChevronDownIcon, ChevronRightIcon, CalendarIcon, CogIcon } from "@heroicons/react/24/outline";
import { BatteryIcon } from "../components/icons/icons";

// Configuration interface
interface Configuration {
  apiEndpoint: string;
  providerId: number;
  apiKey: string;
}

// Schedule data interfaces
interface ScheduleStatus {
  action: string;
  actionAcknowledged: string | null;
  activityLog: string[] | null;
}

interface ScheduleItem {
  id: number;
  executeAt: string;
  action: string;
  providerRegId: number;
  serial: string;
  ts: string;
  provider: string;
  statuses: ScheduleStatus[];
}

// Simple base58 encoding implementation
const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

const encodeBase58 = (buffer: Uint8Array): string => {
  let digits = [0];
  
  for (let i = 0; i < buffer.length; i++) {
    let carry = buffer[i];
    for (let j = 0; j < digits.length; j++) {
      carry += digits[j] << 8;
      digits[j] = carry % 58;
      carry = Math.floor(carry / 58);
    }
    
    while (carry > 0) {
      digits.push(carry % 58);
      carry = Math.floor(carry / 58);
    }
  }
  
  // Convert leading zeros
  let k = 0;
  while (k < buffer.length && buffer[k] === 0) {
    digits.push(0);
    k++;
  }
  
  return digits.reverse().map(digit => BASE58_ALPHABET[digit]).join('');
};

const stringToBase58 = (str: string): string => {
  const buffer = new TextEncoder().encode(str);
  return encodeBase58(buffer);
};

// GraphQL API functions
const fetchScheduleHistory = async (config: Configuration): Promise<ScheduleItem[]> => {
  const query = `
    query schedule {
      ems {
        battery {
          schema(
            providerRegId: ${config.providerId}
            auth: {
              apiKey: {
                key: "${config.apiKey}"
              }
            }
          ) {
            id
            executeAt
            action
            providerRegId
            serial
            ts
            provider
            statuses {
              action
              actionAcknowledged
              activityLog
            }
          }
        }
      }
    }
  `;

  try {
    const response = await fetch(config.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query }),
    });

    const result = await response.json();
    console.log('📡 Schedule history response:', result);
    
    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      throw new Error(result.errors[0]?.message || 'GraphQL error');
    }

    return result.data?.ems?.battery?.schema || [];
  } catch (error) {
    console.error('Failed to fetch schedule history:', error);
    throw error;
  }
};

const registerBatteryAction = async (config: Configuration, power: number, executionTime?: string): Promise<{success: boolean, scheduleId?: string}> => {
  const actionType = power >= 0 ? 'battery.charge' : 'battery.discharge';
  const actionData = {
    power: Math.abs(power),
    actionType
  };
  
  const actionString = JSON.stringify(actionData);
  const encodedAction = stringToBase58(actionString);
  
  // Use provided execution time or default to current time + 1 minute in UTC
  const scheduleTime = executionTime || new Date(Date.now() + 60000).toISOString();
  
  const mutation = `
    mutation registerSchemaAction {
      ems {
        registerSchemaAction(
          providerRegistrationId: ${config.providerId},
          executionTime: "${scheduleTime}",
          action: "${encodedAction}",
          auth: {
            apiKey: {
              key: "${config.apiKey}"
            }
          }
        ) {
          success
          scheduleId
        }
      }
    }
  `;

  try {
    const response = await fetch(config.apiEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ query: mutation }),
    });

    const result = await response.json();
    console.log('🔋 Battery action response:', result);
    
    if (result.errors) {
      console.error('GraphQL errors:', result.errors);
      throw new Error(result.errors[0]?.message || 'GraphQL error');
    }

    return result.data?.ems?.registerSchemaAction || { success: false };
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

// Hardcoded gateway IDs that should have access to the Control page
const CONTROL_ENABLED_GATEWAYS = [
  'f42ea576ac87184445',
  '01233d032a7c838bee'
];

const GatewayControl: FC = () => {
  const { isConnected} = useWalletContext();
  const { gateways, activeGateway, setActiveGateway } = useGateways();
  const { gatewayId } = useParams({ from: '/gateway/$gatewayId' });
  
  // Configuration state
  const [configuration, setConfiguration] = useState<Configuration | null>(null);
  const [configForm, setConfigForm] = useState({
    apiEndpoint: 'https://api-test.srcful.dev/',
    providerId: 16,
    apiKey: ''
  });
  
  // Battery control state
  const [batteryPower, setBatteryPower] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [lastResponse, setLastResponse] = useState<{success: boolean, scheduleId?: string, timestamp?: string} | null>(null);
  
  // Schedule control state
  const [scheduleTime, setScheduleTime] = useState('');
  const [isScheduleLoading, setIsScheduleLoading] = useState(false);
  const [lastScheduleResponse, setLastScheduleResponse] = useState<{success: boolean, scheduleId?: string, timestamp?: string} | null>(null);
  
  // Schedule history state
  const [scheduleHistory, setScheduleHistory] = useState<ScheduleItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (gatewayId && gateways) {
      const gateway = gateways.find(g => g.id === gatewayId);
      if (gateway) {
        setActiveGateway(gateway);
      }
    }
  }, [gatewayId, gateways, setActiveGateway]);


  // Load schedule history on component mount and after successful actions
  const loadScheduleHistory = async () => {
    if (!configuration) return;
    
    setHistoryLoading(true);
    setHistoryError(null);
    
    try {
      const history = await fetchScheduleHistory(configuration);
      // Sort by execution time (most recent first) and limit to 10 items
      const sortedHistory = history
        .sort((a, b) => new Date(b.executeAt).getTime() - new Date(a.executeAt).getTime())
        .slice(0, 10);
      setScheduleHistory(sortedHistory);
    } catch (error) {
      setHistoryError(error instanceof Error ? error.message : 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeGateway && CONTROL_ENABLED_GATEWAYS.includes(activeGateway.id) && configuration) {
      loadScheduleHistory();
      // Set default schedule time to current time + 5 minutes
      const defaultTime = new Date(Date.now() + 5 * 60000);
      const timeString = defaultTime.toTimeString().slice(0, 5); // HH:MM format
      setScheduleTime(timeString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGateway, configuration]);

  if (!isConnected || !activeGateway) return null;

  // Check if this gateway is allowed to access the control page
  if (!CONTROL_ENABLED_GATEWAYS.includes(activeGateway.id)) {
    return (
      <ContentFramework>
        <PageContainer>
          <GatewayPageHeader 
            gatewayName={activeGateway.name}
          />
          <div className="flex flex-col items-center justify-center py-12">
            <WrenchScrewdriverIcon className="w-16 h-16 text-gray-400 mb-4" />
            <h1 className="text-xl font-bold mb-2 text-gray-400">Control Not Available</h1>
            <p className="text-gray-500 text-center">
              Control features are not available for this gateway.
            </p>
          </div>
        </PageContainer>
      </ContentFramework>
    );
  }

  const handleSetBatteryPower = async () => {
    if (!configuration) return;
    
    setIsLoading(true);
    setLastResponse(null);
    
    try {
      console.log('Setting battery power to:', batteryPower);
      const response = await registerBatteryAction(configuration, batteryPower);
      
      setLastResponse({
        ...response,
        timestamp: new Date().toLocaleTimeString()
      });
      
      console.log('API Response:', response);
      
      // Reload history after successful action
      if (response.success) {
        setTimeout(() => loadScheduleHistory(), 1000); // Small delay to allow backend to process
      }
    } catch (error) {
      console.error('Failed to set battery power:', error);
      setLastResponse({
        success: false,
        timestamp: new Date().toLocaleTimeString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleScheduleBatteryPower = async () => {
    if (!configuration) return;
    
    if (!scheduleTime) {
      alert('Please select a time for scheduling');
      return;
    }

    setIsScheduleLoading(true);
    setLastScheduleResponse(null);
    
    try {
      // Create execution time from today's date + selected time
      const today = new Date();
      const [hours, minutes] = scheduleTime.split(':').map(Number);
      const executionDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
      
      // Check if the time is in the past
      if (executionDate <= new Date()) {
        alert('Please select a future time');
        return;
      }
      
      const executionTimeISO = executionDate.toISOString();
      
      console.log('Scheduling battery power to:', batteryPower, 'at:', executionTimeISO);
      const response = await registerBatteryAction(configuration, batteryPower, executionTimeISO);
      
      setLastScheduleResponse({
        ...response,
        timestamp: new Date().toLocaleTimeString()
      });
      
      console.log('Schedule API Response:', response);
      
      // Reload history after successful action
      if (response.success) {
        setTimeout(() => loadScheduleHistory(), 1000); // Small delay to allow backend to process
      }
    } catch (error) {
      console.error('Failed to schedule battery power:', error);
      setLastScheduleResponse({
        success: false,
        timestamp: new Date().toLocaleTimeString()
      });
    } finally {
      setIsScheduleLoading(false);
    }
  };

  const getPowerStatus = () => {
    if (batteryPower > 0) return { status: 'Charging', color: 'text-[#00FF84]' };
    if (batteryPower < 0) return { status: 'Discharging', color: 'text-orange-500' };
    return { status: 'Idle', color: 'text-gray-400' };
  };

  // Helper functions for schedule history
  const parseActionData = (actionString: string) => {
    try {
      const parsed = JSON.parse(actionString);
      return {
        power: parsed.power || 0,
        actionType: parsed.actionType || 'unknown',
        isCharge: parsed.actionType === 'battery.charge',
        isDischarge: parsed.actionType === 'battery.discharge'
      };
    } catch (error) {
      console.error('Failed to parse action data:', actionString, error);
      return { 
        power: 0, 
        actionType: 'unknown',
        isCharge: false,
        isDischarge: false
      };
    }
  };

  const getLatestStatus = (statuses: ScheduleStatus[]) => {
    if (!statuses || statuses.length === 0) return { status: 'UNKNOWN', color: 'text-gray-400', icon: ClockIcon };
    
    // Priority order: ERROR > WARNING > DONE > ACKNOWLEDGED > PLANNED
    const statusPriority = ['ERROR', 'WARNING', 'DONE', 'ACKNOWLEDGED', 'PLANNED'];
    
    for (const statusType of statusPriority) {
      const status = statuses.find(s => s.action === statusType);
      if (status) {
        switch (statusType) {
          case 'DONE':
            return { status: 'DONE', color: 'text-green-400', icon: CheckCircleIcon };
          case 'ERROR':
            return { status: 'ERROR', color: 'text-red-400', icon: XCircleIcon };
          case 'WARNING':
            return { status: 'WARNING', color: 'text-yellow-400', icon: ExclamationTriangleIcon };
          case 'ACKNOWLEDGED':
            return { status: 'ACKNOWLEDGED', color: 'text-blue-400', icon: CheckCircleIcon };
          case 'PLANNED':
            return { status: 'PLANNED', color: 'text-gray-400', icon: ClockIcon };
          default:
            return { status: statusType, color: 'text-gray-400', icon: ClockIcon };
        }
      }
    }
    
    return { status: 'UNKNOWN', color: 'text-gray-400', icon: ClockIcon };
  };

  const formatDateTime = (dateString: string) => {
    try {
      // The dateString from API is in UTC format, ensure it's parsed as UTC
      let utcDate: Date;
      if (dateString.includes('T')) {
        // ISO format - ensure it ends with Z to be parsed as UTC
        const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
        utcDate = new Date(utcString);
      } else {
        // Fallback for other formats
        utcDate = new Date(dateString + ' UTC');
      }
      
      const localTime = utcDate.toLocaleString();
      const utcTime = dateString.includes('T') 
        ? dateString.replace('T', ' ').replace('Z', '').slice(0, 19) + ' UTC'
        : dateString + ' UTC';
      return `${localTime} (${utcTime})`;
    } catch {
      return dateString;
    }
  };

  const formatDateTimeShort = (dateString: string) => {
    try {
      // The dateString from API is in UTC, ensure it's parsed as UTC
      let utcDate: Date;
      if (dateString.includes('T')) {
        // ISO format - ensure it ends with Z to be parsed as UTC
        const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
        utcDate = new Date(utcString);
      } else {
        // Fallback for other formats
        utcDate = new Date(dateString + ' UTC');
      }
      
      return utcDate.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    } catch {
      return dateString;
    }
  };

  const toggleExpanded = (itemId: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const powerStatus = getPowerStatus();

  return (
    <ContentFramework>
      <PageContainer>
        <GatewayPageHeader 
          gatewayName={activeGateway.name}
        />
        
        <div className="mb-6">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <WrenchScrewdriverIcon className="w-6 h-6 text-[#00FF84]" />
            Gateway Control
          </h1>
          <p className="text-gray-400 mt-2">
            Control battery charging and discharging power.
          </p>
        </div>

        {/* Configuration Form */}
        {!configuration && (
          <div className="bg-[#1D1D1D] rounded-[8px] border border-[#292929] p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <CogIcon className="w-6 h-6 text-[#00FF84]" />
              <h2 className="text-lg font-semibold text-white">Configuration</h2>
            </div>
            
            <p className="text-gray-400 mb-6">
              Please configure the API settings before using the controls.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  API Endpoint
                </label>
                <input
                  type="url"
                  value={configForm.apiEndpoint}
                  onChange={(e) => setConfigForm({...configForm, apiEndpoint: e.target.value})}
                  placeholder="https://api-test.srcful.dev/"
                  className="w-full px-3 py-2 bg-[#292929] text-white rounded-lg border border-[#404040] focus:border-[#00FF84] focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Provider ID
                </label>
                <input
                  type="number"
                  value={configForm.providerId}
                  onChange={(e) => setConfigForm({...configForm, providerId: parseInt(e.target.value) || 0})}
                  min="1"
                  className="w-full px-3 py-2 bg-[#292929] text-white rounded-lg border border-[#404040] focus:border-[#00FF84] focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  API Key
                </label>
                <input
                  type="password"
                  value={configForm.apiKey}
                  onChange={(e) => setConfigForm({...configForm, apiKey: e.target.value})}
                  placeholder="Enter your API key"
                  className="w-full px-3 py-2 bg-[#292929] text-white rounded-lg border border-[#404040] focus:border-[#00FF84] focus:outline-none"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  if (configForm.apiEndpoint && configForm.providerId && configForm.apiKey) {
                    setConfiguration(configForm);
                  } else {
                    alert('Please fill in all configuration fields');
                  }
                }}
                disabled={!configForm.apiEndpoint || !configForm.providerId || !configForm.apiKey}
                className={`px-6 py-3 font-semibold rounded-lg transition-colors ${
                  configForm.apiEndpoint && configForm.providerId && configForm.apiKey
                    ? 'bg-[#00FF84] text-black hover:bg-[#00cc6a]'
                    : 'bg-gray-500 text-gray-300 cursor-not-allowed'
                }`}
              >
                Save Configuration
              </button>
            </div>
          </div>
        )}

        {/* Show controls only when configured */}
        {configuration && (
          <>
            {/* Configuration Status */}
            <div className="bg-[#1D1D1D] rounded-[8px] border border-[#292929] p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-400" />
                  <span className="text-green-400 font-medium">Configuration Active</span>
                </div>
                <button
                  onClick={() => setConfiguration(null)}
                  className="px-3 py-1 text-sm bg-[#292929] text-gray-300 rounded hover:bg-[#333333]"
                >
                  Reconfigure
                </button>
              </div>
              <div className="mt-2 text-xs text-gray-400">
                <div>Endpoint: {configuration.apiEndpoint}</div>
                <div>Provider ID: {configuration.providerId}</div>
                <div>API Key: {'*'.repeat(configuration.apiKey.length)}</div>
              </div>
            </div>

        {/* Battery Power Controller */}
        <div className="bg-[#1D1D1D] rounded-[8px] border border-[#292929] p-6">
          <div className="flex items-center gap-3 mb-6">
            <BatteryIcon state="active" className="w-6 h-6" />
            <h2 className="text-lg font-semibold text-white">Battery Power Control</h2>
          </div>
          
          {/* Current Value Display */}
          <div className="text-center mb-6">
            <div className="text-4xl font-bold text-white mb-2">
              {batteryPower > 0 ? '+' : ''}{batteryPower}W
            </div>
            <div className={`text-lg font-medium ${powerStatus.color}`}>
              {powerStatus.status}
            </div>
          </div>

          {/* Power Slider */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-400 mb-2">
              <span>-5000W (Discharging)</span>
              <span>+5000W (Charging)</span>
            </div>
            <input
              type="range"
              min="-5000"
              max="5000"
              step="100"
              value={batteryPower}
              onChange={(e) => setBatteryPower(parseInt(e.target.value))}
              className="w-full h-2 bg-[#292929] rounded-lg appearance-none cursor-pointer slider"
              style={{
                background: `linear-gradient(to right, #ff6b6b 0%, #ff6b6b 45%, #4a4a4a 45%, #4a4a4a 55%, #00ff84 55%, #00ff84 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>-5000</span>
              <span>0</span>
              <span>+5000</span>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Immediate Set Button */}
            <button
              onClick={handleSetBatteryPower}
              disabled={isLoading}
              className={`px-6 py-3 font-semibold rounded-lg transition-colors ${
                isLoading 
                  ? 'bg-gray-500 text-gray-300 cursor-not-allowed' 
                  : 'bg-[#00FF84] text-black hover:bg-[#00cc6a]'
              }`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 inline mr-2 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                  Setting...
                </>
              ) : (
                <>
                  <BoltIcon className="w-5 h-5 inline mr-2" />
                  Set Now (+1 min)
                </>
              )}
            </button>

            {/* Schedule Button */}
            <div className="flex gap-2">
              <input
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                className="flex-1 px-3 py-3 bg-[#292929] text-white rounded-lg border border-[#404040] focus:border-[#00FF84] focus:outline-none"
              />
              <button
                onClick={handleScheduleBatteryPower}
                disabled={isScheduleLoading || !scheduleTime}
                className={`px-4 py-3 font-semibold rounded-lg transition-colors ${
                  isScheduleLoading || !scheduleTime
                    ? 'bg-gray-500 text-gray-300 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isScheduleLoading ? (
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <CalendarIcon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Response Status */}
          {(lastResponse || lastScheduleResponse) && (
            <div className="mt-4 space-y-2">
              {lastResponse && (
                <div className={`p-3 rounded-lg ${
                  lastResponse.success 
                    ? 'bg-green-600/20 border border-green-500/50' 
                    : 'bg-red-600/20 border border-red-500/50'
                }`}>
                  <div className={`text-sm font-medium ${
                    lastResponse.success ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {lastResponse.success ? '✓ Immediate Action' : '✗ Immediate Action Failed'}
                    {lastResponse.timestamp && ` at ${lastResponse.timestamp}`}
                  </div>
                  {lastResponse.scheduleId && (
                    <div className="text-xs text-gray-400 mt-1">
                      Schedule ID: {lastResponse.scheduleId}
                    </div>
                  )}
                </div>
              )}
              
              {lastScheduleResponse && (
                <div className={`p-3 rounded-lg ${
                  lastScheduleResponse.success 
                    ? 'bg-blue-600/20 border border-blue-500/50' 
                    : 'bg-red-600/20 border border-red-500/50'
                }`}>
                  <div className={`text-sm font-medium ${
                    lastScheduleResponse.success ? 'text-blue-400' : 'text-red-400'
                  }`}>
                    {lastScheduleResponse.success ? '✓ Scheduled Action' : '✗ Scheduled Action Failed'}
                    {lastScheduleResponse.timestamp && ` at ${lastScheduleResponse.timestamp}`}
                  </div>
                  {lastScheduleResponse.scheduleId && (
                    <div className="text-xs text-gray-400 mt-1">
                      Schedule ID: {lastScheduleResponse.scheduleId}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Schedule History */}
        <div className="mt-6 bg-[#1D1D1D] rounded-[8px] border border-[#292929] p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <ClockIcon className="w-6 h-6 text-[#00FF84]" />
              <h2 className="text-lg font-semibold text-white">Schedule History</h2>
            </div>
            <button
              onClick={loadScheduleHistory}
              disabled={historyLoading}
              className="px-3 py-1 text-sm bg-[#292929] text-gray-300 rounded hover:bg-[#333333] disabled:opacity-50"
            >
              {historyLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {historyError && (
            <div className="mb-4 p-3 bg-red-600/20 border border-red-500/50 rounded-lg">
              <p className="text-red-400 text-sm">Error: {historyError}</p>
            </div>
          )}

          {historyLoading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 mx-auto border-2 border-gray-300 border-t-[#00FF84] rounded-full animate-spin"></div>
              <p className="text-gray-400 mt-2">Loading schedule history...</p>
            </div>
          ) : scheduleHistory.length === 0 ? (
            <div className="text-center py-8">
              <ClockIcon className="w-12 h-12 mx-auto text-gray-500 mb-2" />
              <p className="text-gray-400">No schedule history available</p>
            </div>
          ) : (
            <div className="space-y-2">
              {scheduleHistory.map((item) => {
                const actionData = parseActionData(item.action);
                const statusInfo = getLatestStatus(item.statuses);
                const StatusIcon = statusInfo.icon;
                const isExpanded = expandedItems.has(item.id);
                const ChevronIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;
                
                return (
                  <div key={item.id} className="bg-[#252525] rounded-lg border border-[#333333] overflow-hidden">
                    {/* Compact Row */}
                    <div 
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-[#2a2a2a] transition-colors"
                      onClick={() => toggleExpanded(item.id)}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <ChevronIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        
                        <span className="text-white font-medium text-sm">#{item.id}</span>
                        
                        <span className={`text-xs px-2 py-1 rounded flex-shrink-0 ${
                          actionData.isCharge
                            ? 'bg-green-600/20 text-green-400' 
                            : actionData.isDischarge
                            ? 'bg-orange-600/20 text-orange-400'
                            : 'bg-gray-600/20 text-gray-400'
                        }`}>
                          {actionData.isCharge ? 'Charge' : actionData.isDischarge ? 'Discharge' : 'Unknown'} {actionData.power}W
                        </span>
                        
                        <span className="text-xs text-gray-400 truncate">
                          {formatTimeAgo(item.executeAt)}
                        </span>
                      </div>
                      
                      <div className={`flex items-center gap-1 ${statusInfo.color} flex-shrink-0`}>
                        <StatusIcon className="w-4 h-4" />
                        <span className="text-xs font-medium">{statusInfo.status}</span>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-[#333333] bg-[#1f1f1f]">
                        <div className="pt-3 space-y-3">
                          {/* Action Details */}
                          <div className="bg-[#1a1a1a] rounded p-3">
                            <div className="text-xs text-gray-500 mb-2">Action Details</div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <div className="text-gray-500 text-xs">Type</div>
                                <div className="text-gray-300">{actionData.actionType}</div>
                              </div>
                              <div>
                                <div className="text-gray-500 text-xs">Power</div>
                                <div className="text-gray-300">{actionData.power}W</div>
                              </div>
                            </div>
                            <div className="mt-2">
                              <div className="text-gray-500 text-xs">Raw Action</div>
                              <div className="text-gray-400 text-xs font-mono bg-[#0f0f0f] p-1 rounded mt-1">
                                {item.action}
                              </div>
                            </div>
                          </div>

                          {/* Timestamps */}
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <div className="text-gray-500 text-xs">Scheduled</div>
                              <div className="text-gray-300">{formatDateTime(item.executeAt)}</div>
                            </div>
                            <div>
                              <div className="text-gray-500 text-xs">Created</div>
                              <div className="text-gray-300">{formatDateTime(item.ts)}</div>
                            </div>
                          </div>

                          {/* Status Timeline */}
                          {item.statuses && item.statuses.length > 0 && (
                            <div>
                              <div className="text-xs text-gray-500 mb-2">Status Timeline</div>
                              <div className="space-y-1">
                                {item.statuses
                                  .filter(status => status.actionAcknowledged)
                                  .sort((a, b) => new Date(a.actionAcknowledged!).getTime() - new Date(b.actionAcknowledged!).getTime())
                                  .map((status, index) => (
                                    <div key={index} className="flex items-center justify-between text-xs">
                                      <span className={`px-2 py-1 rounded text-xs ${
                                        status.action === 'DONE' ? 'bg-green-600/20 text-green-400' :
                                        status.action === 'ERROR' ? 'bg-red-600/20 text-red-400' :
                                        status.action === 'WARNING' ? 'bg-yellow-600/20 text-yellow-400' :
                                        status.action === 'ACKNOWLEDGED' ? 'bg-blue-600/20 text-blue-400' :
                                        'bg-gray-600/20 text-gray-400'
                                      }`}>
                                        {status.action}
                                      </span>
                                      <span 
                                        className="text-gray-500 cursor-help" 
                                        title={formatDateTime(status.actionAcknowledged!)}
                                      >
                                        {formatDateTimeShort(status.actionAcknowledged!)}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}

                          {/* Activity Logs for Errors/Warnings */}
                          {item.statuses.some(s => (s.action === 'ERROR' || s.action === 'WARNING') && s.activityLog) && (
                            <div>
                              <div className="text-xs text-gray-500 mb-2">Activity Log</div>
                              <div className="bg-[#1a1a1a] rounded p-2 max-h-32 overflow-y-auto">
                                {item.statuses
                                  .filter(s => (s.action === 'ERROR' || s.action === 'WARNING') && s.activityLog)
                                  .flatMap(s => s.activityLog!)
                                  .map((log, index) => (
                                    <div key={index} className="text-xs text-gray-400 mb-1 last:mb-0">
                                      {log}
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Info Note */}
        <div className="mt-6 p-4 bg-blue-600/20 border border-blue-500/50 rounded-lg">
          <p className="text-blue-400 text-sm">
            <strong>Note:</strong> Positive values indicate charging, negative values indicate discharging. 
            Power is measured in watts (W). Use "Set Now" for immediate execution (+1 minute) or select a specific time to schedule for later today.
          </p>
        </div>

            {/* Debug Info */}
            <div className="mt-4 p-3 bg-gray-800/50 border border-gray-600/50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-300 mb-2">Debug Information</h3>
              <div className="text-xs text-gray-400 space-y-1">
                <div>API Endpoint: {configuration.apiEndpoint}</div>
                <div>Provider Registration ID: {configuration.providerId}</div>
                <div>Current Power Setting: {batteryPower}W</div>
                <div>Action Type: {batteryPower >= 0 ? 'battery.charge' : 'battery.discharge'}</div>
                <div>Execution Time: {new Date(Date.now() + 60000).toISOString()}</div>
              </div>
            </div>
          </>
        )}
        
      </PageContainer>
    </ContentFramework>
  );
};

export default GatewayControl; 